/*
 * measurement-range.js
 * RealList — converts the bust / waist / hips / inseam checkbox facets into
 * dual-handle range sliders, while reusing Shopify's native (server-side)
 * filtering. Progressive enhancement: if this script fails or JS is off, the
 * original checkbox lists remain fully functional.
 *
 * How it works: each measurement value is already stored as a discrete facet
 * value (e.g. 32"), and garments that fit a range are stored as the full list
 * of inches they fit. The slider simply checks every value inside the selected
 * window and lets Dawn's existing facet form submit it. Result: "show me items
 * that fit this range" with correct overlap semantics and working pagination.
 */
(function () {
  'use strict';

  // Only these metafield keys become sliders. Matched against the LAST segment
  // of the filter param name, e.g. "filter.p.m.custom.bust" -> "bust".
  var KEYS = {
    bust: 'Bust',
    waist: 'Waist',
    hips: 'Hips',
    inseam: 'Inseam'
  };

  var UNIT = '"';
  var STEP = 0.5; // catalogue uses whole and half inches

  // ---- helpers -------------------------------------------------------------

  // Parse a facet value into a numeric [min, max] span.
  // Handles: 34"  |  34" (trailing space)  |  27.5"  |  42-44"  |  42 – 44"
  // Returns null for non-numeric values (e.g. "Free" / "One Size").
  function parseSpan(raw) {
    if (raw == null) return null;
    var s = String(raw)
      .replace(/["'“”″\s]/g, '') // strip quotes/inch marks/whitespace
      .replace(/[–—]/g, '-'); // en/em dash -> hyphen
    var m = s.match(/^(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)$/);
    if (m) {
      var a = parseFloat(m[1]);
      var b = parseFloat(m[2]);
      if (isNaN(a) || isNaN(b)) return null;
      return [Math.min(a, b), Math.max(a, b)];
    }
    if (!/^\d/.test(s)) return null;
    var n = parseFloat(s);
    if (isNaN(n)) return null;
    return [n, n];
  }

  function keyFromName(name) {
    if (!name) return null;
    var seg = name.split('.').pop().trim().toLowerCase();
    return Object.prototype.hasOwnProperty.call(KEYS, seg) ? seg : null;
  }

  function fmt(n) {
    // 28 -> 28", 27.5 -> 27.5"
    var v = Math.round(n * 10) / 10;
    return (Number.isInteger(v) ? String(v) : v.toFixed(1)) + UNIT;
  }

  function floorTo(n, step) { return Math.floor(n / step) * step; }
  function ceilTo(n, step) { return Math.ceil(n / step) * step; }

  // Find the <form> a node belongs to (desktop or mobile facet form).
  function formOf(node) {
    var f = node.closest && node.closest('form');
    return f || document.querySelector('#FacetFiltersForm') || null;
  }

  // ---- core transform ------------------------------------------------------

  // Transform a single facet group (a .js-filter <details>) into a slider.
  function transformGroup(group) {
    if (!group) return;
    // Idempotency by DOM presence, NOT a property flag: Dawn's AJAX replaces
    // this element's innerHTML (wiping the injected slider) while keeping the
    // element object, so a flag would wrongly block rebuilding after filtering.
    if (group.querySelector('.mrange')) return;

    var checkboxes = Array.prototype.slice.call(
      group.querySelectorAll('input[type="checkbox"][name]')
    );
    if (!checkboxes.length) return;

    var key = keyFromName(checkboxes[0].getAttribute('name'));
    if (!key) return; // not a measurement facet — leave it alone

    // Build the numeric model. Keep a handle on each checkbox + its span.
    var items = [];
    var freeItems = []; // "Free" becomes the far-right end of the slider scale
    var keepLis = []; // any OTHER non-numeric values (e.g. "One Size") stay as checkboxes
    checkboxes.forEach(function (cb) {
      var raw = cb.getAttribute('value');
      var span = parseSpan(raw);
      var li = cb.closest('li');
      if (span) {
        items.push({ cb: cb, min: span[0], max: span[1], li: li });
      } else if (raw != null && String(raw).trim().toLowerCase() === 'free') {
        freeItems.push({ cb: cb, li: li });
      } else if (li) {
        keepLis.push(li);
      }
    });

    // Need at least two distinct numeric points to make a slider meaningful.
    if (items.length < 2) return;

    var domainMin = floorTo(Math.min.apply(null, items.map(function (i) { return i.min; })), STEP);
    var domainMax = ceilTo(Math.max.apply(null, items.map(function (i) { return i.max; })), STEP);
    if (!(domainMax > domainMin)) return;

    // "Free" (free size / one-size) sits at the far-right end of the scale, one
    // step past the numeric maximum, so the rightmost handle position reads
    // "Free" — intuitively the largest / most accommodating size. domainMax stays
    // the numeric ceiling; sliderMax is the full track including the Free slot.
    var hasFree = freeItems.length > 0;
    var sliderMax = hasFree ? domainMax + STEP : domainMax;

    // Initial handle positions from any currently-active (checked) values so
    // the slider reflects the URL state after a page load / AJAX re-render.
    var active = items.filter(function (i) { return i.cb.checked; });
    var freeChecked = freeItems.some(function (f) { return f.cb.checked; });
    var fromVal, toVal;
    if (!active.length && !freeChecked) {
      // Nothing selected — span the whole scale (including Free) = no filter.
      fromVal = domainMin; toVal = sliderMax;
    } else if (!active.length && freeChecked) {
      // Only "Free" selected — collapse both handles onto the Free slot.
      fromVal = sliderMax; toVal = sliderMax;
    } else {
      fromVal = Math.max(domainMin, floorTo(Math.min.apply(null, active.map(function (i) { return i.min; })), STEP));
      toVal = Math.min(domainMax, ceilTo(Math.max.apply(null, active.map(function (i) { return i.max; })), STEP));
      if (freeChecked) toVal = sliderMax; // selection also reaches the Free slot
    }

    // Mark this filter so its checkbox list is hidden by CSS *immediately* on
    // every (re)render — including Shopify's AJAX re-render — before the browser
    // lays it out. This attribute lives on the <details>, which Shopify keeps
    // across re-renders (it only swaps the inner HTML), so the fresh checkbox
    // list never flashes at full height and no empty space is reserved.
    // The checkboxes stay in the DOM (display:none still submits with the form).
    // "Free" is now driven by the slider's right end, so its checkbox stays
    // hidden; only OTHER non-numeric values (e.g. "One Size") remain visible.
    group.setAttribute('data-mrange', key);
    keepLis.forEach(function (li) { li.classList.add('mrange-keep'); });

    var display = group.querySelector('.facets__display') || group;

    // Build slider UI.
    var wrap = document.createElement('div');
    wrap.className = 'mrange';
    wrap.setAttribute('data-key', key);
    wrap.innerHTML =
      '<div class="mrange__readout">' +
        '<span class="mrange__from"></span>' +
        '<span class="mrange__dash">–</span>' +
        '<span class="mrange__to"></span>' +
      '</div>' +
      '<div class="mrange__slider">' +
        '<div class="mrange__rail"></div>' +
        '<div class="mrange__fill"></div>' +
        '<input type="range" class="mrange__handle mrange__handle--from" ' +
          'min="' + domainMin + '" max="' + sliderMax + '" step="' + STEP + '" ' +
          'aria-label="' + KEYS[key] + ' minimum">' +
        '<input type="range" class="mrange__handle mrange__handle--to" ' +
          'min="' + domainMin + '" max="' + sliderMax + '" step="' + STEP + '" ' +
          'aria-label="' + KEYS[key] + ' maximum">' +
      '</div>' +
      '<div class="mrange__bounds">' +
        '<span>' + fmt(domainMin) + '</span><span>' + (hasFree ? 'Free' : fmt(domainMax)) + '</span>' +
      '</div>';

    // Insert the slider above the (now hidden) list. Covers desktop
    // (.facets__list) and the mobile drawer (.mobile-facets__list).
    var list = group.querySelector('.facets__list, .mobile-facets__list');
    if (list) list.parentNode.insertBefore(wrap, list);
    else display.insertBefore(wrap, display.firstChild);

    var fromH = wrap.querySelector('.mrange__handle--from');
    var toH = wrap.querySelector('.mrange__handle--to');
    var fill = wrap.querySelector('.mrange__fill');
    var fromLbl = wrap.querySelector('.mrange__from');
    var toLbl = wrap.querySelector('.mrange__to');

    fromH.value = fromVal;
    toH.value = toVal;

    // Label a handle value: the far-right slot reads "Free", everything else a number.
    function labelFor(v) {
      return (hasFree && v >= sliderMax) ? 'Free' : fmt(v);
    }

    function paint() {
      var lo = parseFloat(fromH.value);
      var hi = parseFloat(toH.value);
      var span = sliderMax - domainMin || 1;
      var l = ((lo - domainMin) / span) * 100;
      var r = ((hi - domainMin) / span) * 100;
      fill.style.left = l + '%';
      fill.style.width = Math.max(0, r - l) + '%';
      fromLbl.textContent = labelFor(lo);
      toLbl.textContent = labelFor(hi);
      // Keep the top handle clickable when the two sit on top of each other.
      toH.style.zIndex = (lo >= sliderMax) ? 6 : 5;
    }

    function clamp() {
      var lo = parseFloat(fromH.value);
      var hi = parseFloat(toH.value);
      if (lo > hi) {
        // Push the handle being moved rather than swapping identities.
        if (document.activeElement === fromH) { hi = lo; toH.value = hi; }
        else { lo = hi; fromH.value = lo; }
      }
    }

    // Apply the selection to the underlying checkboxes and let Dawn submit.
    function apply() {
      var lo = parseFloat(fromH.value);
      var hi = parseFloat(toH.value);
      var isFull = (lo <= domainMin && hi >= sliderMax);
      // Numeric overlap is capped at the numeric ceiling so the extra Free slot
      // never shrinks the numeric window; "Free" is included only when the top
      // handle actually reaches the far-right Free slot.
      var effHi = Math.min(hi, domainMax);
      var wantFree = hasFree && !isFull && (hi >= sliderMax);
      var changed = false;

      items.forEach(function (i) {
        // Overlap test: garment fit-span intersects the selected window.
        var want = !isFull && !i.cb.disabled && (i.min <= effHi && i.max >= lo);
        if (i.cb.checked !== want) {
          i.cb.checked = want;
          changed = true;
        }
      });

      freeItems.forEach(function (f) {
        if (f.cb.disabled) return;
        if (f.cb.checked !== wantFree) {
          f.cb.checked = wantFree;
          changed = true;
        }
      });

      if (!changed) return;
      var form = formOf(group);
      if (form) {
        // Mirror a real user interaction so Dawn's facet form re-renders.
        form.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }

    fromH.addEventListener('input', function () { clamp(); paint(); });
    toH.addEventListener('input', function () { clamp(); paint(); });
    fromH.addEventListener('change', function () { clamp(); paint(); apply(); });
    toH.addEventListener('change', function () { clamp(); paint(); apply(); });

    paint();
  }

  // Collapse the per-inch active-filter pills into nothing (the slider readout
  // already shows the selected range). Runs after every render.
  // Is the price filter effectively "no filter" (the full range)? Dawn submits
  // the price min/max on EVERY filter change — even when the shopper never
  // touched price — which produces a redundant "Price: $0 – $max" active pill
  // that filters nothing. Detect that so we can hide only the spurious pill and
  // never a price range the shopper actually set.
  function priceIsFullRange() {
    var gte = document.querySelector('input[name="filter.v.price.gte"]');
    var lte = document.querySelector('input[name="filter.v.price.lte"]');
    if (!gte || !lte) return false;
    var min = parseFloat(gte.getAttribute('min') || '0');
    var max = parseFloat(lte.getAttribute('max'));
    if (isNaN(max)) return false;
    var g = gte.value === '' ? min : parseFloat(gte.value);
    var l = lte.value === '' ? max : parseFloat(lte.value);
    return g <= min && l >= max;
  }

  function hidePills(root) {
    var labels = Object.keys(KEYS).map(function (k) { return KEYS[k].toLowerCase(); });
    (root || document).querySelectorAll('.active-facets__button').forEach(function (btn) {
      // Hide the <facet-remove> WRAPPER, not just the inner <a>. The wrapper is
      // a flex element with vertical padding, so hiding only the <a> left each
      // pill reserving ~14px — with a wide range (many values) that stacked up
      // into a large empty gap in the filter panel.
      var wrap = btn.closest('facet-remove') || btn;
      if (wrap.__mrangeChecked) return;
      var first = btn.querySelector('.active-facets__button-inner > span');
      if (!first) { wrap.__mrangeChecked = true; return; }
      var txt = first.textContent.replace(':', '').trim().toLowerCase();
      // Measurement pills always collapse; the price pill only when it's the
      // spurious full-range one (a real, narrowed price filter stays visible).
      if (labels.indexOf(txt) !== -1 || (txt === 'price' && priceIsFullRange())) {
        wrap.style.display = 'none';
      }
      wrap.__mrangeChecked = true;
    });
  }

  function scan() {
    document.querySelectorAll('.js-filter').forEach(transformGroup);
    hidePills();
  }

  // Re-run after Dawn replaces facet / grid markup via AJAX.
  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    // Use setTimeout, NOT requestAnimationFrame: rAF is paused while the tab is
    // backgrounded, which would leave the slider un-rebuilt (and pills un-hidden)
    // after an AJAX filter until the tab regains focus. setTimeout fires either
    // way; the 50ms delay coalesces one render's burst of mutations into one scan.
    setTimeout(function () {
      scheduled = false;
      scan();
    }, 50);
  }

  function init() {
    scan();
    // Observe the whole document so we catch Shopify's AJAX re-render in EVERY
    // layout — desktop sidebar, the mobile/drawer facet form, the active-facet
    // pills, and the product grid. (A narrower set of targets missed the drawer
    // form, so the slider wasn't rebuilt there.) Cheap: reacting only to added
    // nodes, throttled to one scan per frame, and each scan is fully guarded.
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].type === 'childList' && mutations[i].addedNodes.length) {
          schedule();
          return;
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
